import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, PhotoGrid, StarRating } from 'components/ds';
import { useAlert } from 'contexts/AlertContext';
import { placePhotosPath } from 'hooks/usePlaceReviews';
import type { PlaceFeed } from 'hooks/usePlaceReviews';
import { usePlacePhotoUpload } from 'hooks/usePlacePhotoUpload';
import { StorePhotoSlots } from './StorePhotoSlots';
import type { StoreSlot } from './StorePhotoSlots';
import { VisitPhotoDetail } from './VisitPhotoDetail';
import { VisitPhotoWrite } from './VisitPhotoWrite';
import styles from './PlacePhotoPeek.module.css';

const PEEK = 6;

/**
 * 장소 사진 미리보기 — 가게 사진 네 자리와 방문 사진 몇 장.
 *
 * 모임 상세에서 두 자리에 나옵니다(제목 아래 · 장소 시트). 두 번 불러오지 않도록
 * 데이터는 부르는 쪽이 usePlaceReviews 로 한 번 받아 feed 로 넘깁니다.
 * 다 보고 쓰는 일은 [전체보기]로 넘어간 장소 화면에서 합니다.
 */
export const PlacePhotoPeek = ({
    placeId, placeName, meetingId, canAdd, allowWrite, feed,
}: {
    placeId: string;
    placeName: string;
    meetingId?: string;
    /** 사진·후기를 올릴 수 있는 때인가 — 판단은 부르는 쪽이 합니다 */
    canAdd?: boolean;
    /** 후기 쓰기 버튼을 여기서 보일지. 시트 안에서는 시트가 겹치므로 끕니다 */
    allowWrite?: boolean;
    feed: PlaceFeed;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { storePhotos, posts, rating, reviewCount, reload, toggleHelpful } = feed;
    const [writing, setWriting] = useState(false);
    const { busy, uploadStorePhoto, submitVisitPost } = usePlacePhotoUpload({ placeId, placeName, meetingId });
    const [detail, setDetail] = useState<number | null>(null);

    const handlePick = useCallback(async (slot: StoreSlot, files: File[]) => {
        const ok = await uploadStorePhoto(slot, files);
        await showAlert({ message: t(ok ? 'visitPhoto.uploaded' : 'visitPhoto.uploadFail') });
        if (ok) reload();
    }, [uploadStorePhoto, showAlert, t, reload]);

    const openAll = () => navigate(placePhotosPath(placeId, { placeName, meetingId, canAdd }));

    /* 미리보기는 사진이 있는 후기만 씁니다 — 별점만 남긴 후기를 격자에 넣으면 빈 칸이 됩니다.
       상세로 넘길 때는 원본 목록의 자리를 줘서 좌우 이동이 어긋나지 않게 합니다. */
    const peek = posts.filter(p => p.photos.length > 0).slice(0, PEEK);
    const nothingYet = storePhotos.length === 0 && posts.length === 0;

    // 아직 아무것도 없고 올릴 수도 없으면 자리만 차지합니다
    if (nothingYet && !canAdd) return null;

    return (
        <div className={styles.wrap}>
            {rating ? (
                <div className={styles.rating}>
                    <StarRating value={rating} count={reviewCount} source="CREW" />
                </div>
            ) : null}

            <div className={styles.block}>
                <span className={styles.label}>{t('storePhoto.sectionTitle')}</span>
                <StorePhotoSlots photos={storePhotos} canAdd={canAdd && !busy} onPick={handlePick} />
            </div>

            {peek.length > 0 && (
                <div className={styles.block}>
                    <span className={styles.label}>
                        {t('visitPhoto.title')}
                        <span className={styles.count}>{posts.length < 999 ? posts.length : '999+'}</span>
                    </span>
                    <PhotoGrid
                        photos={peek.map(p => ({ id: String(p.postId), url: p.photos[0] }))}
                        onSelect={(_photo, i) => setDetail(posts.indexOf(peek[i]))}
                    />
                </div>
            )}

            <div className={styles.actions}>
                {allowWrite && canAdd && (
                    <Button block onClick={() => setWriting(true)}>{t('visitPhoto.writeTitle')}</Button>
                )}
                <Button block variant="secondary" onClick={openAll}>{t('visitPhoto.seeAll')}</Button>
            </div>

            {allowWrite && (
                <VisitPhotoWrite
                    open={writing}
                    onClose={() => setWriting(false)}
                    placeName={placeName}
                    submitting={busy}
                    onSubmit={async (draft) => {
                        const { ok, photosFailed } = await submitVisitPost(draft);
                        setWriting(false);
                        await showAlert({
                            message: t(!ok ? 'visitPhoto.uploadFail'
                                : photosFailed > 0 ? 'visitPhoto.postedPhotoFail'
                                : 'visitPhoto.uploaded'),
                        });
                        if (ok) reload();
                    }}
                />
            )}

            <VisitPhotoDetail
                open={detail !== null}
                posts={posts}
                index={detail ?? 0}
                onIndexChange={setDetail}
                onClose={() => setDetail(null)}
                onHelpful={toggleHelpful}
            />
        </div>
    );
};
